import { Button, DatePicker, Input, Select, Text, Stack, VStack } from '@genial/components';
import { Utils } from '@genial/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  SignedInNavigationScreenNavigationProp,
  SignedInRootStackParamList,
} from '../../../../../../../../../../routes/types';
import { usePrivatePensionAssetController } from '../../../../../../../../../BatchTicket/components/DynamicAsset/hooks/products/PrivatePension';
import { PrivatePensionAccountHolder } from '../../../../../../../../../BatchTicket/components/DynamicAsset/hooks/products/PrivatePension/types';
import { PrivatePensionModalContainer } from '../PrivatePensionModalContainer';
import { PrivatePensionModalHeader } from '../PrivatePensionModalHeader';

export namespace PrivatePensionAccountHolderModalTypes {
  export interface Props {}
}

const PrivatePensionAccountHolderModalComponent: React.FC<
  PrivatePensionAccountHolderModalTypes.Props
> = () => {
  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();
  const route =
    useRoute<RouteProp<SignedInRootStackParamList, 'CustomerDetailPrivatePensionAccountHolder'>>();
  const { offer, accountId, accountNumber } = route.params;
  const offerController = usePrivatePensionAssetController(offer, accountId, accountNumber);
  const { accountHolder, bankOptions, relatedPartyOptions, bankConfigurations } = offerController;
  const [bankIdLocal, setBankIdLocal] = React.useState<number | undefined>(
    accountHolder?.bankId || undefined
  );

  const bankConfiguration = bankIdLocal ? bankConfigurations?.[bankIdLocal] : undefined;

  const accountHolderSchema = useMemo(() => {
    let validator = z.object({
      name: z
        .string({
          required_error: 'Nome é obrigatório',
        })
        .refine(
          (value) => {
            const regex = /^[\p{L}0-9']+(?: [\p{L}0-9']+)+$/u;
            return regex.test(value);
          },
          {
            message:
              'Nome e sobrenome são obrigatórios, não pode conter espaços consecutivos e não pode conter caracteres especiais',
          }
        ),
      email: z
        .string({
          required_error: 'Email é obrigatório',
        })
        .nonempty('Email é obrigatório')
        .email({
          message: 'Email inválido',
        }),
      cpf: z
        .string({
          required_error: 'CPF é obrigatório',
        })
        .refine((value) => Utils.Validate.validateCpf(value), {
          message: 'CPF inválido',
        }),
      bankId: z.number({
        required_error: 'Nome do banco é obrigatório',
      }),
      relatedPartyId: z.number({
        required_error: 'Parentesco é obrigatório',
      }),
      birthDate: z
        .date({
          required_error: 'Data de nascimento é obrigatória',
        })
        .max(new Date(), {
          message: 'Data de nascimento não pode ser futura',
        }),
    });

    if (bankConfiguration?.bankAccountNumber.required) {
      validator = validator.extend({
        bankAccountNumber: z
          .string({
            required_error: 'Número da conta bancária é obrigatório',
          })
          .min(
            bankConfiguration?.bankAccountNumber.minLength || 0,
            `Número da conta bancária deve conter no mínimo ${bankConfiguration?.bankAccountNumber.minLength} caractere(s)`
          )
          .max(
            bankConfiguration?.bankAccountNumber.maxLength || 0,
            `Número da conta bancária deve conter no máximo ${bankConfiguration?.bankAccountNumber.maxLength} caractere(s)`
          )
          .nonempty('Número da conta bancária é obrigatório')
          .refine((value) => !value.match(/^\D$/), {
            message: 'Número da conta bancária deve conter apenas números',
          })
          .transform((value) => Number(value)),
      });
    }

    if (bankConfiguration?.bankAccountDigit.required) {
      validator = validator.extend({
        bankAccountDigit: z
          .string({
            required_error: 'Dígito da conta bancária é obrigatório',
          })
          .min(
            bankConfiguration?.bankAccountDigit.minLength || 0,
            `Dígito da conta bancária deve conter no mínimo ${bankConfiguration?.bankAccountDigit.minLength} caractere(s)`
          )
          .max(
            bankConfiguration?.bankAccountDigit.maxLength || 0,
            `Dígito da conta bancária deve conter no máximo ${bankConfiguration?.bankAccountDigit.maxLength} caractere(s)`
          )
          .nonempty('Dígito da conta bancária é obrigatório')
          .refine((value) => !value.match(/^\D$/), {
            message: 'Dígito da conta bancária deve conter apenas números',
          })
          .transform((value) => Number(value)),
      });
    }

    if (bankConfiguration?.bankAgencyNumber.required) {
      validator = validator.extend({
        bankAgencyNumber: z
          .string({
            required_error: 'Número da agência bancária é obrigatório',
          })
          .min(
            bankConfiguration?.bankAgencyNumber.minLength || 0,
            `Número da agência bancária deve conter no mínimo ${bankConfiguration?.bankAgencyNumber.minLength} caractere(s)`
          )
          .max(
            bankConfiguration?.bankAgencyNumber.maxLength || 0,
            `Número da agência bancária deve conter no máximo ${bankConfiguration?.bankAgencyNumber.maxLength} caractere(s)`
          )
          .nonempty('Número da agência bancária é obrigatório')
          .refine((value) => !value.match(/^\D$/), {
            message: 'Número da agência bancária deve conter apenas números',
          })
          .transform((value) => Number(value)),
      });
    }

    if (bankConfiguration?.bankAgencyDigit.required) {
      validator = validator.extend({
        bankAgencyDigit: z
          .string({
            required_error: 'Dígito da agência bancária é obrigatório',
          })
          .min(
            bankConfiguration?.bankAgencyDigit.minLength || 0,
            `Dígito da agência bancária deve conter no mínimo ${bankConfiguration?.bankAgencyDigit.minLength} caractere(s)`
          )
          .max(
            bankConfiguration?.bankAgencyDigit.maxLength || 0,
            `Dígito da agência bancária deve conter no máximo ${bankConfiguration?.bankAgencyDigit.maxLength} caractere(s)`
          )
          .nonempty('Dígito da agência bancária é obrigatório')
          .refine((value) => !value.match(/^\D$/), {
            message: 'Dígito da agência bancária deve conter apenas números',
          })
          .transform((value) => Number(value)),
      });
    }

    return validator;
  }, [bankConfiguration]);

  const bankAccountNumberDisabled = !bankIdLocal || !bankConfiguration?.bankAccountDigit.required;
  const bankAccountDigitDisabled = !bankIdLocal || !bankConfiguration?.bankAccountDigit.required;
  const bankAgencyNumberDisabled = !bankIdLocal || !bankConfiguration?.bankAgencyNumber.required;
  const bankAgencyDigitDisabled = !bankIdLocal || !bankConfiguration?.bankAgencyDigit.required;

  const formEdit = useForm({
    resolver: zodResolver(accountHolderSchema),
    mode: 'all',
    defaultValues: {
      name: accountHolder?.name || '',
      email: accountHolder?.email || '',
      cpf: accountHolder?.cpf || '',
      bankAccountNumber: accountHolder?.bankAccountNumber?.toString() || undefined,
      bankAccountDigit: accountHolder?.bankAccountDigit?.toString() || undefined,
      bankAgencyNumber: accountHolder?.bankAgencyNumber?.toString() || undefined,
      bankAgencyDigit: accountHolder?.bankAgencyDigit?.toString() || undefined,
      bankId: accountHolder?.bankId || undefined,
      relatedPartyId: accountHolder?.relatedPartyId || undefined,
      birthDate: accountHolder?.birthDate || undefined,
      paymentMethod: accountHolder?.paymentMethod || undefined,
    },
  });

  const {
    setValue,
    watch,
    handleSubmit,
    formState: { isValid, errors },
    control,
    resetField,
  } = formEdit;

  const formBirthDate = watch('birthDate');
  const formBankId = watch('bankId');
  const formRelatedPartyId = watch('relatedPartyId');

  const handleBankChange = useCallback(
    (bankId: string) => {
      setBankIdLocal(Number(bankId));
      resetField('bankAccountNumber', {
        defaultValue: '',
      });
      resetField('bankAccountDigit', {
        defaultValue: '',
      });
      resetField('bankAgencyNumber', {
        defaultValue: '',
      });
      resetField('bankAgencyDigit', {
        defaultValue: '',
      });
    },
    [resetField]
  );

  const handleBirthDateChange = useCallback(
    (date: Date) => {
      setValue('birthDate', date, {
        shouldValidate: true,
      });
    },
    [setValue]
  );

  const handleBirthDateClear = useCallback(() => {
    setValue('birthDate', undefined, {
      shouldValidate: true,
    });
  }, [setValue]);

  const handleRelatedPartyChange = useCallback(
    (relatedPartyId: string) => {
      setValue('relatedPartyId', Number(relatedPartyId), {
        shouldValidate: true,
      });
    },
    [setValue]
  );

  const handleCancel = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const handleSaveAccountHolder = handleSubmit(() => {
    offerController.setAccountHolder(
      formEdit.getValues() as unknown as PrivatePensionAccountHolder
    );

    navigation.goBack();
  });

  useEffect(() => {
    if (!bankIdLocal) {
      return;
    }

    setValue('bankId', bankIdLocal, {
      shouldValidate: true,
    });
  }, [bankIdLocal, setValue]);

  return (
    <PrivatePensionModalContainer>
      <PrivatePensionModalHeader title="Editar responsável financeiro" onClose={handleCancel} />
      <VStack space="stack.nano" width="100%">
        <VStack space="stack.quarck">
          <Text variant="semibold" fontSize="md">
            Nome do responsável
          </Text>
          <Input
            control={control}
            name="name"
            variant="filled"
            placeholder="Digite o nome do responsável"
            autoComplete="off"
            hasShadow={false}
            error={errors.name}
          />
        </VStack>
        <Stack direction={{ base: 'column', sm: 'row' }} width="100%" space="stack.nano">
          <VStack space="stack.quarck" flex={1}>
            <Text variant="semibold" fontSize="md">
              Email
            </Text>
            <Input
              control={control}
              name="email"
              variant="filled"
              placeholder="Digite o email do responsável"
              autoComplete="off"
              hasShadow={false}
              error={errors.email}
            />
          </VStack>
          <VStack space="stack.quarck" flex={1}>
            <Text variant="semibold" fontSize="md">
              CPF
            </Text>
            <Input
              control={control}
              name="cpf"
              variant="filled"
              placeholder="Digite o CPF do responsável"
              autoComplete="off"
              hasShadow={false}
              error={errors.cpf}
            />
          </VStack>
        </Stack>
        <Stack direction={{ base: 'column', sm: 'row' }} width="100%" space="stack.nano" zIndex={3}>
          <VStack space="stack.quarck" flex={1}>
            <Text variant="semibold" fontSize="md">
              Data de nascimento
            </Text>
            <DatePicker
              onSelectDate={handleBirthDateChange}
              onClear={handleBirthDateClear}
              date={formBirthDate}
              availableRange={{
                future: new Date(),
              }}
              error={errors.birthDate}
            />
          </VStack>
          <VStack space="stack.quarck" flex={1}>
            <Text variant="semibold" fontSize="md">
              Banco
            </Text>
            <Select
              onValueChange={handleBankChange}
              placeholder="Selecione o banco"
              options={bankOptions}
              selectedValue={formBankId?.toString()}
              maxOptionPerRender={5}
              error={errors.bankId}
            />
          </VStack>
        </Stack>
        <Stack direction={{ base: 'column', sm: 'row' }} width="100%" space="stack.nano">
          <VStack space="stack.quarck" flex={1}>
            <Text variant="semibold" fontSize="md">
              Agência
            </Text>
            <Input
              control={control}
              name="bankAgencyNumber"
              variant="filled"
              placeholder="Digite a agência"
              autoComplete="off"
              hasShadow={false}
              keyboardType="number-pad"
              mask={Utils.Mask.Numbers}
              isDisabled={bankAgencyNumberDisabled}
              error={errors.bankAgencyNumber}
            />
          </VStack>
          <VStack space="stack.quarck" flex={1}>
            <Text variant="semibold" fontSize="md">
              Dígito agência
            </Text>
            <Input
              control={control}
              name="bankAgencyDigit"
              variant="filled"
              placeholder="Digite o dígito da agência"
              autoComplete="off"
              hasShadow={false}
              keyboardType="number-pad"
              mask={Utils.Mask.Numbers}
              isDisabled={bankAgencyDigitDisabled}
              error={errors.bankAgencyDigit}
            />
          </VStack>
        </Stack>
        <Stack direction={{ base: 'column', sm: 'row' }} width="100%" space="stack.nano">
          <VStack space="stack.quarck" flex={1}>
            <Text variant="semibold" fontSize="md">
              Conta
            </Text>
            <Input
              control={control}
              name="bankAccountNumber"
              variant="filled"
              placeholder="Digite a conta"
              autoComplete="off"
              hasShadow={false}
              keyboardType="number-pad"
              mask={Utils.Mask.Numbers}
              isDisabled={bankAccountNumberDisabled}
              error={errors.bankAccountNumber}
            />
          </VStack>
          <VStack space="stack.quarck" flex={1}>
            <Text variant="semibold" fontSize="md">
              Dígito conta
            </Text>
            <Input
              control={control}
              name="bankAccountDigit"
              variant="filled"
              placeholder="Digite o dígito da conta"
              autoComplete="off"
              hasShadow={false}
              keyboardType="number-pad"
              mask={Utils.Mask.Numbers}
              isDisabled={bankAccountDigitDisabled}
              error={errors.bankAccountDigit}
            />
          </VStack>
        </Stack>
        <VStack space="stack.quarck">
          <Text variant="semibold" fontSize="md">
            Parentesco
          </Text>
          <Select
            onValueChange={handleRelatedPartyChange}
            placeholder="Selecione o parentesco"
            options={relatedPartyOptions}
            selectedValue={formRelatedPartyId?.toString()}
            maxOptionPerRender={5}
            error={errors.relatedPartyId}
          />
        </VStack>

        <Stack
          direction={{ base: 'column', md: 'row' }}
          width="100%"
          justifyContent={{ base: 'center', md: 'flex-end' }}
          flexDirection={{ base: 'column-reverse', md: 'row' }}
          space="stack.nano">
          <Button
            variant="secondary"
            onPress={handleCancel}
            testID="private-pension-portability-modal-cancel-button">
            Cancelar
          </Button>
          <Button
            isDisabled={!isValid}
            testID="private-pension-portability-modal-confirm-button"
            onPress={handleSaveAccountHolder}>
            Confirmar
          </Button>
        </Stack>
      </VStack>
    </PrivatePensionModalContainer>
  );
};

export const PrivatePensionAccountHolderModal = React.memo(
  PrivatePensionAccountHolderModalComponent
);
